import { clearQuoteHighlight, focusQuote } from "./anchors";
import { ApiError, fetchMarkdown } from "./api";
import { state } from "./app-state";
import { refreshComments } from "./comment-lists";
import { escapeHtml } from "./comments";
import { getDocById, MOBILE_BREAKPOINT, resolveDocId, STORAGE } from "./constants";
import { renderIcons, ui } from "./dom";
// document-view.ts ⇄ editor.ts 互相引用，调用均在运行期函数体内，安全。
import { guardLeaveEditor, updateEditAffordances } from "./editor";
import { enhanceReader, renderMarkdown } from "./markdown";
import { setActiveDoc } from "./navigation";
import { closePanels } from "./panels";
import { hideSelectionAction } from "./selection-ui";
import { hardLogout } from "./session-ui";
import { adjacentDocs, saveScrollPosition } from "./state";
import { showToast } from "./toast";
import type { ReviewComment, TocItem } from "./types";
import { countReadableCharacters, formatWordCount } from "./word-count";

let currentToc: TocItem[] = [];
let loadToken = 0;
let scrollTimer = 0;
/** scrollspy 的标题位置缓存：滚动时读缓存，布局变化时整体重算。 */
let headingOffsets: { id: string; top: number }[] = [];
let activeHeadingId = "";
let scrollFrame = 0;
let readerResizeObserver: ResizeObserver | null = null;

function renderToc(items: TocItem[]): void {
  currentToc = items;
  activeHeadingId = "";
  ui.tocNav.innerHTML = items.length
    ? items.map((item) => `<button type="button" class="level-${item.level}" data-heading="${item.id}" title="${item.title.replace(/"/g, "&quot;")}">${item.title}</button>`).join("")
    : '<div class="empty-state toc-empty">本页暂无章节</div>';
}

/** 标题绝对位置只在内容或布局变化时量一次，滚动路径上零布局读取。 */
export function measureHeadingOffsets(): void {
  // 编辑态 reader 隐藏时全部位置都是 0,量出来只会污染缓存。
  if (ui.reader.hidden) return;
  headingOffsets = currentToc
    .map((item) => {
      const heading = document.getElementById(item.id);
      if (!heading) return null;
      return { id: item.id, top: heading.getBoundingClientRect().top + window.scrollY };
    })
    .filter((entry): entry is { id: string; top: number } => entry !== null);
}

function observeReaderLayout(): void {
  if (!("ResizeObserver" in window)) return;
  readerResizeObserver ??= new ResizeObserver(() => {
    measureHeadingOffsets();
    updateReadingPosition();
  });
  readerResizeObserver.disconnect();
  readerResizeObserver.observe(ui.reader);
}

function docPagerHtml(id: string): string {
  const { prev, next } = adjacentDocs(id);
  if (!prev && !next) return "";
  const link = (doc: NonNullable<typeof prev>, direction: "prev" | "next"): string => `
    <button type="button" class="doc-pager-link doc-pager-link--${direction}" data-action="goto-doc" data-doc="${escapeHtml(doc.id)}">
      ${direction === "prev" ? '<i data-lucide="chevron-left" aria-hidden="true"></i>' : ""}
      <span class="doc-pager-text">
        <small>${direction === "prev" ? "上一篇" : "下一篇"}</small>
        <strong>${escapeHtml(doc.shortTitle || doc.title)}</strong>
      </span>
      ${direction === "next" ? '<i data-lucide="chevron-right" aria-hidden="true"></i>' : ""}
    </button>`;
  return `${prev ? link(prev, "prev") : '<span class="doc-pager-spacer"></span>'}
    ${next ? link(next, "next") : '<span class="doc-pager-spacer"></span>'}`;
}

function readerSkeleton(): string {
  return `<div class="reader-skeleton" aria-label="正在加载">
    <span></span><span></span><span></span><span></span><span></span>
  </div>`;
}

function readerError(message: string): string {
  return `<div class="reader-error">
    <strong>文档暂时无法打开</strong>
    <span>${message}</span><br />
    <button class="secondary-button" type="button" data-action="retry-doc">重新加载</button>
  </div>`;
}

function restoreDocumentScroll(id: string): void {
  const position = state.scrollPositions[id] || 0;
  requestAnimationFrame(() => requestAnimationFrame(() => {
    window.scrollTo({ top: position, behavior: "auto" });
    updateReadingPosition();
  }));
}

export async function openDocument(id: string, restoreScroll = true): Promise<void> {
  const resolved = resolveDocId(id);
  const doc = getDocById(resolved);
  if (!doc) return;
  // 编辑态守卫必须在一切状态变更之前：用户取消离开时当前视图不受影响。
  if (!(await guardLeaveEditor())) return;
  const token = ++loadToken;

  if (state.view === "document" && state.currentDocId !== resolved) {
    saveScrollPosition(state, state.currentDocId, window.scrollY);
  }
  state.view = "document";
  state.currentDocId = resolved;
  state.quote = null;
  localStorage.setItem(STORAGE.currentDoc, resolved);
  hideSelectionAction();
  clearQuoteHighlight(ui.reader);

  ui.documentView.hidden = false;
  ui.boardView.hidden = true;
  ui.tocSection.hidden = false;
  ui.readingProgress.hidden = false;
  ui.commentsBtn.hidden = false;
  ui.fontScaleBtn.hidden = false;
  ui.documentsNavBtn.classList.add("active");
  ui.boardNavBtn.classList.remove("active");
  ui.viewKicker.textContent = doc.group === "draft" ? "正文阅读" : "文档审阅";
  ui.viewTitle.textContent = doc.title;
  ui.wordCount.textContent = "";
  ui.wordCount.hidden = true;
  ui.reader.setAttribute("aria-busy", "true");
  ui.reader.classList.toggle("reader--fiction", doc.group === "draft");
  ui.readerStatus.textContent = `正在加载${doc.title}`;
  setActiveDoc(resolved);
  closePanels(false);

  ui.reader.innerHTML = readerSkeleton();
  ui.docPager.hidden = true;
  ui.docPager.innerHTML = "";
  try {
    let html = state.markdownCache.get(resolved);
    if (!html) {
      const source = await fetchMarkdown(doc.file);
      state.contentRelease.set(resolved, source.release);
      html = renderMarkdown(source.text);
      state.markdownCache.set(resolved, html);
    }
    if (token !== loadToken) return;
    ui.reader.innerHTML = html;
    renderToc(enhanceReader(ui.reader));
    ui.wordCount.textContent = formatWordCount(countReadableCharacters(ui.reader.textContent || ""));
    ui.wordCount.hidden = false;
    ui.reader.removeAttribute("aria-busy");
    ui.readerStatus.textContent = `${doc.title}已加载`;
    renderDocPager(resolved);
    measureHeadingOffsets();
    observeReaderLayout();
    updateEditAffordances();
    await refreshComments({ showSkeleton: true });
    if (restoreScroll) restoreDocumentScroll(resolved);
    else window.scrollTo({ top: 0, behavior: "auto" });
  } catch (error) {
    if (token !== loadToken) return;
    if (error instanceof ApiError && error.status === 401) {
      hardLogout();
      return;
    }
    ui.reader.innerHTML = readerError(error instanceof Error ? error.message : "请稍后重试");
    ui.wordCount.textContent = "";
    ui.wordCount.hidden = true;
    ui.reader.removeAttribute("aria-busy");
    ui.readerStatus.textContent = `${doc.title}加载失败`;
    renderDocPager(resolved);
    updateEditAffordances();
    renderToc([]);
  }
}

function renderDocPager(id: string): void {
  const doc = getDocById(id);
  ui.docPager.classList.toggle("doc-pager-shell--fiction", doc?.group === "draft");
  ui.docPager.innerHTML = docPagerHtml(id);
  ui.docPager.hidden = ui.docPager.childElementCount === 0;
  renderIcons();
}

export function updateReadingPosition(): void {
  if (state.view !== "document" || ui.documentView.hidden) return;
  // 编辑态下 reader 隐藏，进度无意义，也不能把编辑时的滚动写进阅读位置。
  if (state.editor) return;
  if (scrollFrame) return;
  scrollFrame = window.requestAnimationFrame(() => {
    scrollFrame = 0;
    applyReadingPosition();
  });
}

function applyReadingPosition(): void {
  if (state.view !== "document" || ui.documentView.hidden || state.editor) return;
  const rect = ui.reader.getBoundingClientRect();
  const topOffset = 90;
  const scrollable = Math.max(1, ui.reader.offsetHeight - window.innerHeight + topOffset);
  const progress = Math.max(0, Math.min(1, (-rect.top + topOffset) / scrollable));
  const percent = Math.round(progress * 100);
  ui.progressText.textContent = `${percent}%`;
  ui.progressBar.style.width = `${percent}%`;

  // 读缓存而不是逐标题 getBoundingClientRect；只有活动项变化才写 DOM。
  const threshold = window.scrollY + topOffset + 30;
  let activeId = "";
  for (const heading of headingOffsets) {
    if (heading.top <= threshold) activeId = heading.id;
    else break;
  }
  if (activeId !== activeHeadingId) {
    activeHeadingId = activeId;
    let activeButton: HTMLButtonElement | null = null;
    ui.tocNav.querySelectorAll<HTMLButtonElement>("button[data-heading]").forEach((button) => {
      const isActive = button.dataset.heading === activeId;
      button.classList.toggle("active", isActive);
      if (isActive) activeButton = button;
    });
    // 长目录里让当前项保持可见（仅桌面侧栏；移动端抽屉关闭时无意义）。
    if (activeButton && window.innerWidth > MOBILE_BREAKPOINT) {
      (activeButton as HTMLButtonElement).scrollIntoView({ block: "nearest" });
    }
  }

  window.clearTimeout(scrollTimer);
  scrollTimer = window.setTimeout(() => {
    if (state.view === "document") saveScrollPosition(state, state.currentDocId, window.scrollY);
  }, 220);
}

export async function goToComment(comment: ReviewComment): Promise<void> {
  if (state.editor) {
    showToast("编辑模式下无法定位原文，请先退出编辑");
    return;
  }
  if (!comment.quote_text || comment.doc_id === "home") {
    showToast("这是一条整体意见，没有对应的原文位置");
    return;
  }
  const targetDoc = resolveDocId(comment.doc_id);
  if (state.currentDocId !== targetDoc || state.view !== "document") {
    await openDocument(targetDoc, false);
  }
  closePanels(false);
  const found = focusQuote(ui.reader, {
    text: comment.quote_text,
    prefix: comment.quote_prefix || "",
    suffix: comment.quote_suffix || "",
  });
  if (!found) showToast("原文可能已经修改，暂时无法精确定位", "error");
}
